// TODO - should not need to import Dexie here - all Dexie logic should be abstracted away
import { NotesDB } from '@annote/core/dbs/notesDb';
import Dexie from 'dexie';
import type { DexieDatabase } from '@annote/core/dbs/dexieDb';
import type { ServerDB } from '@annote/core/dbs/types';
import type { Note, Profile } from '@annote/types';
import { ensureOnlyNoteFields } from '@annote/core/utils/notes';
import { debounce } from 'lodash';

export class DexieSyncService {
  private serverDB: ServerDB;

  private clientDB: DexieDatabase;

  private db: NotesDB;

  // Configurable batch sizes - because supabase sux and can't handle more than a few at a time...
  // TODO - get rid of supabase, it's dumb pg_net calls and simplify this code
  private static readonly SYNC_BATCH_SIZE = 5; // Conservative for sync operations

  private static readonly MAX_RETRIES = 3;

  constructor(
    private readonly dexieDB: DexieDatabase, // private readonly onReAuth?: () => Promise<void | boolean>,
  ) {
    if (!dexieDB.serverDB) {
      throw new Error('DexieDB not initialized with serverDB');
    }
    this.serverDB = dexieDB.serverDB;
    this.clientDB = dexieDB;
    this.db = dexieDB.db;
  }

  // Make sure that the clientDB and the serverDB have the same exact userId
  private ensureSameUser = (): void => {
    const sameUser = this.serverDB.userId && this.serverDB.userId === this.clientDB.userId;
    if (sameUser) return;

    throw new Error(
      `SYNC - UserIDs are not the same for sync.  client user: ${this.clientDB.userId} 
      | server user: ${this.serverDB.userId}`,
    );
  };

  public syncWithServer = async (): Promise<void> => {
    if (!navigator.onLine) {
      console.log('SYNCING: skipping as user is offline');
      return;
    }

    if (!(await this.serverDB.isAuthenticated())) {
      console.warn('SYNCING: server not authenticated - skipping sync');
      await this.serverDB.onReauth();
      return;
    }

    // Make sure the client and server have the same userId
    this.ensureSameUser();

    console.log('SYNCING: notes with server', this.serverDB.userId, this.clientDB.userId);

    // TODO - make one RPC to handle all of this instead of 3 separate calls
    await this.sendUpdates();
    await this.fetchUpdates();
    await this.syncProfile();
  };

  public debouncedSyncWithServer = debounce(this.syncWithServer, 300);

  // Fallback for when the bulk update fails - sync one at a time
  private syncIndividualNote = async (note: Note, iteration: number = 0): Promise<void> => {
    if (!navigator.onLine) {
      console.log('sync - no internet connection - skipping individual note sync');
      return;
    }

    console.log('Warning - retrying sync of note', note.id, 'iteration', iteration);
    try {
      await this.serverDB.updateNotes([note]);
      // TODO - abstract this out of the sync service - this logic should be in DexieDB
      await this.db.changesToNotes.delete(note.id);
    } catch (innerError: unknown) {
      // Handle foreign key constraint errors immediately (before retry logic)
      if (innerError && typeof innerError === 'object' && 'message' in innerError) {
        const errorMessage = String(innerError.message);
        if (
          (errorMessage.includes('23503') || errorMessage.includes('violates foreign key constraint')) &&
          note.createdFromId
        ) {
          console.log(
            'Note',
            note.id,
            'has invalid createdFromId reference',
            note.createdFromId,
            'removing createdFromId and retrying',
          );
          console.log('TODO - we should probably not do this - ideally we would not have to');
          const noteWithoutCreatedFromId = { ...note, createdFromId: undefined } as Note;
          // Also update the local database to prevent future sync issues
          await this.db.changesToNotes.put(noteWithoutCreatedFromId);
          await this.syncIndividualNote(noteWithoutCreatedFromId, iteration + 1);
          return;
        }
      }

      // Convert to Error for other error handling
      const error = innerError instanceof Error ? innerError : new Error('Unknown sync error', { cause: innerError });

      // Check for timeout errors - retry with exponential backoff
      if (DexieSyncService.isTimeoutOrDatabaseError(error) && iteration < DexieSyncService.MAX_RETRIES) {
        const backoffMs = Math.min(1000 * 2 ** iteration, 10000); // Cap at 10 seconds
        console.log(
          `Timeout error syncing note ${note.id}, attempt ${iteration}, retrying in ${backoffMs}ms`,
          error.message,
        );
        await new Promise((resolve) => {
          setTimeout(resolve, backoffMs);
        });
        await this.syncIndividualNote(note, iteration + 1);
        return;
      }

      if (iteration < DexieSyncService.MAX_RETRIES) {
        console.log('ERROR syncing note', note.id, 'attempt', iteration, 'retrying in 1 second', error);
        await new Promise((resolve) => {
          setTimeout(resolve, 1000);
        });
        await this.syncIndividualNote(note, iteration + 1);
      } else {
        throw new Error(`Error syncing note after ${iteration} attempts`, { cause: innerError });
      }
    }
  };

  // TODO - make this an RPC that sends and receives updates in one call
  // (ideally it gets profiles, and also deletes as a list of ids)
  private sendUpdates = async (): Promise<void> => {
    if (!navigator.onLine) {
      console.log('sync - no internet connection - skipping sync');
      return;
    }

    try {
      await this.db.transaction('rw', this.db.changesToNotes, async () => {
        const noteChanges = await this.db.changesToNotes.toArray();
        if (noteChanges.length > 100) {
          console.log('sync - too many note changes to sync!  Something must be wrong', noteChanges);
          throw new Error('Too many note changes to sync!  Something must be wrong', { cause: noteChanges });
        }
        if (noteChanges.length > 0) {
          console.log('sync - sending to server:', noteChanges);

          // Batch updates to avoid timeout issues - send in chunks of 20
          const BATCH_SIZE = DexieSyncService.SYNC_BATCH_SIZE;
          if (noteChanges.length > BATCH_SIZE) {
            console.log(`sync - batching ${noteChanges.length} notes into chunks of ${BATCH_SIZE}`);

            // Sort notes to avoid foreign key constraint violations
            // Process notes without createdFromId first, then notes with createdFromId
            const sortedNotes = [...noteChanges].sort((a, b) => {
              // Notes without createdFromId come first
              if (!a.createdFromId && b.createdFromId) return -1;
              if (a.createdFromId && !b.createdFromId) return 1;
              return 0;
            });

            const batches: Note[][] = [];
            for (let i = 0; i < sortedNotes.length; i += BATCH_SIZE) {
              batches.push(sortedNotes.slice(i, i + BATCH_SIZE));
            }

            // Process batches sequentially to avoid overwhelming the server
            const startTime = performance.now();
            try {
              for (let i = 0; i < batches.length; i += 1) {
                const batch = batches[i];
                const batchStartTime = performance.now();
                console.log(`sync - sending batch ${i + 1}/${batches.length} (${batch.length} notes)`);
                // eslint-disable-next-line no-await-in-loop
                await Dexie.waitFor(this.serverDB.updateNotes(batch));
                const batchTime = performance.now() - batchStartTime;
                console.log(`sync - batch ${i + 1} completed in ${batchTime.toFixed(2)}ms`);
              }
              const totalTime = performance.now() - startTime;
              console.log(`sync - all ${batches.length} batches completed in ${totalTime.toFixed(2)}ms`);
            } catch (batchError: unknown) {
              console.error('Batch operation failed, will trigger individual sync fallback:', batchError);
              // Re-throw the error to trigger the main catch block's individual sync logic
              throw batchError;
            }
          } else {
            await Dexie.waitFor(this.serverDB.updateNotes(noteChanges));
          }

          await this.db.changesToNotes.clear(); // Clear only after successful sync
        }
      });
    } catch (error: unknown) {
      // Handle all errors uniformly - don't check instanceof early
      // Most error handling below works with any object that has the right properties

      // Check for 401 Unauthorized errors
      if (error && typeof error === 'object' && DexieSyncService.isUnauthorizedError(error as Error)) {
        console.error('401 Unauthorized error during sync - triggering reauth', error);
        await this.serverDB.onReauth();
        return; // Exit early after triggering reauth
      }

      // Check for timeout errors and other database issues that should trigger individual sync
      if (error && typeof error === 'object' && DexieSyncService.isTimeoutOrDatabaseError(error as Error)) {
        console.error('Timeout or database error during bulk sync - switching to individual note sync', error);
        // @ts-expect-error error might have message property
        console.log('Error message:', error.message);
        console.log('Error type:', typeof error, error.constructor?.name);
        // Fall through to individual sync logic below
      } else if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        (String(error.message).includes('57014') || JSON.stringify(error).includes('57014'))
      ) {
        console.error('PostgreSQL query cancellation error (57014) - switching to individual note sync', error);
        // Fall through to individual sync logic below
      } else if (error && typeof error === 'object' && 'code' in error && error.code === '42501') {
        console.error('Error syncing notes - permission denied - likely not authed', JSON.stringify(error));

        const {
          data: { session },
          error: sessionError,
        } = await this.serverDB.db.auth.getSession();
        if (sessionError) console.error('Error sending notes - could not get session', sessionError);

        if (!session) {
          console.log('Error sending notes - confirmed session not found');
          console.error('Session not found - trying to reauth');
          this.serverDB.onReauth();
        }
      } else if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        String(error.message).includes('Too many note changes to sync!')
      ) {
        console.log('Too many note changes to sync!  Something must be wrong', error);
        throw error;
      }

      // Last resort: if error is not an object with expected properties, wrap it
      if (!(error && typeof error === 'object' && 'message' in error)) {
        console.log('sync - error is of unknown type - ', error, 'type of error', typeof error);
        throw new Error('Error syncing notes - unknown error type', { cause: error });
      }

      console.error('Error syncing notes JSON here', JSON.stringify(error));
      console.log('try syncing each note individually if the bulk update fails');

      const rawNoteChanges = await this.db.changesToNotes.toArray();
      if (this.serverDB.userId === undefined) {
        await this.serverDB.onReauth();
        throw new Error('syncing - Server DB not initialized during transaction', { cause: this.serverDB });
      }
      // ensure authed userId
      const noteChanges = rawNoteChanges
        .map((change) => {
          return { ...change, userId: this.serverDB.userId };
        })
        .sort((a, b) => {
          // put the notes without createdFromId first
          if (!a.createdFromId && b.createdFromId) return -1;
          if (a.createdFromId && !b.createdFromId) return 1;
          return 0;
        });

      await noteChanges.reduce(async (previousPromise, change) => {
        await previousPromise;
        // on occasion errors where an extra field is in the note pop up.  make sure they're cleaned
        const cleanedChange = ensureOnlyNoteFields(change);
        await this.syncIndividualNote(cleanedChange);
      }, Promise.resolve()); // Initial value is a resolved promise
    }
  };

  // TODO - combine as a response to sendUpdates, and return deletes as a list of ids (not full note)
  private fetchUpdates = async (): Promise<void> => {
    try {
      const newSyncTime = new Date().toISOString();
      const lastSyncedAt = await this.clientDB.getLastSyncTime();
      console.log('FETCHING: last synced at', lastSyncedAt);
      // Update notes since the last sync with the server
      const updatedNotes = await this.serverDB.getNotesSince(lastSyncedAt);
      this.clientDB.updateLastSyncTime(newSyncTime);

      if (updatedNotes.length === 0) return;

      // remove deleted notes from the local database
      // TODO - later make a get_note_changes rpc that only returns ids for deleted notes
      // and have RLS filter out the deleted notes
      const deletedNotes = updatedNotes.filter((note) => note.deletedAt !== null);
      if (deletedNotes.length > 0) {
        console.log('DELETING: got these deleted notes from the server - deleting in local db', deletedNotes);
        await this.db.notes.bulkDelete(deletedNotes.map((note) => note.id));
      }

      const notes = updatedNotes.filter((note) => note.deletedAt === null);
      if (notes.length > 0) {
        console.log('received these notes from server', notes);
        await this.db.notes.bulkPut(notes);
        // Search module is currently disabled
        // if (notes.length > 10 && this.clientDB.searchModule) {
        //   // rebuild the search index if there are a lot of note
        //   await this.clientDB.searchModule.buildIndex();
        // }
      }
    } catch (error: unknown) {
      if (error instanceof Error && DexieSyncService.isUnauthorizedError(error)) {
        console.error('401 Unauthorized error during fetchUpdates - triggering reauth', error);
        await this.serverDB.onReauth();
        return; // Exit early after triggering reauth
      }
      // Re-throw other errors
      throw error;
    }
  };

  private syncProfile = async (): Promise<Profile> => {
    // check if the client profile has been updated - if so send it to the server
    const clientProfile = await this.clientDB.getProfile();
    if (clientProfile && clientProfile.syncedAt === null) {
      await this.serverDB.updateProfile(clientProfile);
    }

    // Get the latest profile from the server and set it in the clientDB
    try {
      // Force refresh to get fresh data from server during sync
      const profile = await this.serverDB.getProfile(true);
      if (profile) {
        await this.clientDB.setMetadata('profile', profile);
        return profile;
      }
      throw new Error('Sync profile - No profile returned from server');
    } catch (error: unknown) {
      if (error instanceof Error && DexieSyncService.isUnauthorizedError(error)) {
        console.error('401 Unauthorized error during syncProfile - triggering reauth', error);
        await this.serverDB.onReauth();
        throw error; // Re-throw to maintain the expected behavior
      }
      // Re-throw other errors
      throw error;
    }
  };

  private static isUnauthorizedError(error: Error): boolean {
    // First check for HTTP 401 status - this is the most direct and reliable method
    const errorWithStatus = error as {
      status?: number;
      statusCode?: number;
    };
    // Direct status check - covers most HTTP errors including fetch errors
    if (errorWithStatus.status === 401 || errorWithStatus.statusCode === 401) {
      return true;
    }
    if (error.message.includes('violates row-level security policy')) return true;

    return false;
  }

  // check for known errors that should trigger fallbacks into individual note sync mode
  private static isTimeoutOrDatabaseError(error: Error): boolean {
    const errorMessage = error.message.toLowerCase();
    const errorString = JSON.stringify(error).toLowerCase();

    // Check for timeout errors (be more aggressive)
    if (errorMessage.includes('canceling statement due to statement timeout')) return true;
    if (errorMessage.includes('timeout')) return true;
    if (errorMessage.includes('connection timeout')) return true;
    if (errorMessage.includes('statement timeout')) return true;
    if (errorMessage.includes('query timeout')) return true;
    if (errorMessage.includes('request timeout')) return true;
    if (errorMessage.includes('operation timeout')) return true;

    // Check for cancellation errors
    if (errorMessage.includes('canceling statement')) return true;
    if (errorMessage.includes('query canceled')) return true;
    if (errorMessage.includes('operation canceled')) return true;
    if (errorMessage.includes('request canceled')) return true;

    // Check in the full error object as well
    if (errorString.includes('timeout')) return true;
    if (errorString.includes('cancel')) return true;

    // Check for foreign key constraint violations (like createdFromId references)
    if (errorMessage.includes('23503')) return true; // foreign_key_violation
    if (errorMessage.includes('violates foreign key constraint')) return true;
    if (errorMessage.includes('notes_createdfromid_fkey')) return true;

    // Check for other database errors that should trigger individual sync
    if (errorMessage.includes('database connection')) return true;
    if (errorMessage.includes('connection lost')) return true;
    if (errorMessage.includes('server closed the connection')) return true;

    // Check for PostgreSQL specific timeout/connection errors
    if (errorMessage.includes('57014')) return true; // query_canceled
    if (errorMessage.includes('08000')) return true; // connection_exception
    if (errorMessage.includes('08003')) return true; // connection_does_not_exist
    if (errorMessage.includes('08006')) return true; // connection_failure

    // Check for Supabase specific errors
    if (errorMessage.includes('functionshttperror')) return true;
    if (errorMessage.includes('functionsfetcherror')) return true;

    return false;
  }
}
